import { useEffect, useState } from 'react';
import localCrashes from '../data/planeCrashes.json';
import localLoot from '../data/lootContainers.json';
import type { MapPoint } from '../types';

/**
 * ---------------------------------------------------------------------
 * DATA SHAPE the map expects (after parsing):
 *   { id: string, name: string, x: number, z: number }[]
 * x/z are raw DayZ world coordinates (Y / height is intentionally dropped).
 * ---------------------------------------------------------------------
 */

interface RawCrashEvent {
  EventPositions?: { Position: [number, number, number]; PositionName: string }[];
}

interface RawContainer {
  debug_name: string;
  ContainerPosition?: { Position?: number[] };
}

interface RawContainersPool {
  ContainersPresetsPool?: {
    Presets?: { Containers?: RawContainer[] }[];
  };
}

// Turns a raw PlaneCrashEvent-style export into marker points.
// Accepts either a single event object or an array of them.
export function parseCrashEvents(raw: RawCrashEvent | RawCrashEvent[]): MapPoint[] {
  const events = Array.isArray(raw) ? raw : [raw];
  const out: MapPoint[] = [];
  events.forEach((ev) => {
    (ev.EventPositions || []).forEach((p, i) => {
      const [x, , z] = p.Position;
      out.push({
        id: `crash-${out.length}-${i}`,
        name: p.PositionName,
        x,
        z,
      });
    });
  });
  return out;
}

// Turns a raw ContainersPresetsPool export into marker points.
export function parseLootContainers(raw: RawContainersPool): MapPoint[] {
  const presets = raw?.ContainersPresetsPool?.Presets || [];
  const out: MapPoint[] = [];
  presets.forEach((preset) => {
    (preset.Containers || []).forEach((c) => {
      const pos = c.ContainerPosition?.Position;
      if (!pos || pos.length < 3) return; // some containers use RandomPositionWithRadius, no fixed point
      const [x, , z] = pos;
      out.push({
        id: `loot-${out.length}`,
        name: c.debug_name,
        x,
        z,
      });
    });
  });
  return out;
}

/**
 * ---------------------------------------------------------------------
 * GOOGLE DRIVE LOADING (for later — wire this in once files are on Drive)
 * ---------------------------------------------------------------------
 * Option A — file shared as "Anyone with the link":
 *   const url = `https://drive.google.com/uc?export=download&id=${fileId}`;
 *   Works for small JSON files without extra setup. Google sometimes
 *   inserts an HTML "virus scan" confirmation page for larger files,
 *   so this is best for files under a few MB.
 *
 * Option B — Drive API v3 (more reliable, needs an API key):
 *   const url = `https://www.googleapis.com/drive/v3/files/${fileId}` +
 *               `?alt=media&key=${API_KEY}`;
 *
 * Either way, once you have raw JSON back, run it through
 * parseCrashEvents() / parseLootContainers() above — same shape either
 * source gives you the same marker points.
 */
async function fetchDriveJson<T>(fileId: string): Promise<T> {
  const url = `https://drive.google.com/uc?export=download&id=${fileId}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Drive fetch failed: ${res.status}`);
  return res.json();
}

// Set these once your files are uploaded to Drive and shared publicly.
const DRIVE_FILE_IDS: { crashes: string | null; loot: string | null } = {
  crashes: null, // e.g. '1AbCдEfGhIjKlMnOpQrStUvWxYz'
  loot: null,
};

interface UseMapDataResult {
  crashes: MapPoint[];
  loot: MapPoint[];
  loading: boolean;
  error: Error | null;
}

export function useMapData(): UseMapDataResult {
  const [crashes, setCrashes] = useState<MapPoint[] | null>(null);
  const [loot, setLoot] = useState<MapPoint[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        if (DRIVE_FILE_IDS.crashes && DRIVE_FILE_IDS.loot) {
          const [rawCrash, rawLoot] = await Promise.all([
            fetchDriveJson<RawCrashEvent | RawCrashEvent[]>(DRIVE_FILE_IDS.crashes),
            fetchDriveJson<RawContainersPool>(DRIVE_FILE_IDS.loot),
          ]);
          if (cancelled) return;
          setCrashes(parseCrashEvents(rawCrash));
          setLoot(parseLootContainers(rawLoot));
        } else {
          // Fallback: bundled local JSON (already pre-parsed, x/z only).
          if (cancelled) return;
          setCrashes(localCrashes as MapPoint[]);
          setLoot(localLoot as MapPoint[]);
        }
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { crashes: crashes || [], loot: loot || [], loading, error };
}
